import { scoreGuestNameMatch } from './guestNameMatch';
import { filterPlayersForHostSearch } from './clubPlayerLinkDedup';
import { isHostSelectSignup } from './signupSelection';

export const PASTE_ROW_STATUS = {
  READY: 'ready',
  NEEDS_MATCH: 'needs_match',
  ALREADY_ON_SESSION: 'already_on_session',
  BLOCKED: 'blocked',
};

export const PASTE_MEMBERSHIP = {
  NONE: 'none',
  CONFIRMED: 'confirmed',
  WAITLIST: 'waitlist',
  CANCELLED: 'cancelled',
  ON_SESSION: 'on_session',
};

export const PASTE_PLACEMENT = {
  CONFIRMED: 'confirmed',
  WAITLIST: 'waitlist',
  ADD: 'add',
  ALREADY_ON_SESSION: 'already_on_session',
  BLOCKED: 'blocked',
};

export function normalizeNameKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/** Levenshtein distance. Names are short, so a simple DP table is enough. */
export function editDistance(a, b) {
  const left = String(a || '');
  const right = String(b || '');
  if (left === right) return 0;
  if (!left) return right.length;
  if (!right) return left.length;

  const prev = new Array(right.length + 1);
  const curr = new Array(right.length + 1);
  for (let j = 0; j <= right.length; j += 1) prev[j] = j;

  for (let i = 1; i <= left.length; i += 1) {
    curr[0] = i;
    for (let j = 1; j <= right.length; j += 1) {
      const cost = left[i - 1] === right[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= right.length; j += 1) prev[j] = curr[j];
  }

  return prev[right.length];
}

/**
 * Typo-close names: one edit if the shorter token is at least 4 letters,
 * two edits if it is at least 8. Exact equals are not "close".
 */
export function namesAreClose(a, b, { maxDistance = null } = {}) {
  const left = normalizeNameKey(a);
  const right = normalizeNameKey(b);
  if (!left || !right || left === right) return false;

  const shortest = Math.min(left.length, right.length);
  const allowed =
    maxDistance != null
      ? maxDistance
      : shortest >= 8
        ? 2
        : shortest >= 4
          ? 1
          : 0;
  if (allowed < 1) return false;
  return editDistance(left, right) <= allowed;
}

export function playerNameParts(player) {
  const first = normalizeNameKey(player?.first_name || player?.firstName || '');
  const last = normalizeNameKey(player?.last_name || player?.lastName || '');
  const full = [first, last].filter(Boolean).join(' ');
  const reversed = [last, first].filter(Boolean).join(' ');
  return { first, last, full, reversed };
}

/**
 * Parse one pasted line into first/last parts.
 * Tab-separated spreadsheet rows keep the first cell. A single comma is Last, First.
 */
export function parsePastedNameLine(rawLine) {
  const cell = String(rawLine || '').split('\t')[0].trim();
  if (!cell) return null;

  let first = '';
  let last = '';

  const commaCount = (cell.match(/,/g) || []).length;
  if (commaCount === 1) {
    const [lastPart, firstPart] = cell.split(',');
    last = (lastPart || '').trim();
    first = (firstPart || '').trim();
  } else {
    const tokens = cell.replace(/,/g, ' ').split(/\s+/).filter(Boolean);
    if (tokens.length === 1) {
      first = tokens[0];
    } else if (tokens.length > 1) {
      first = tokens[0];
      last = tokens.slice(1).join(' ');
    }
  }

  if (!first && !last) return null;

  return {
    raw: cell,
    first,
    last,
    normalized: normalizeNameKey([first, last].filter(Boolean).join(' ')),
  };
}

export function parseRosterPasteText(text) {
  const seen = new Set();
  const lines = [];

  String(text || '')
    .split(/\r?\n/)
    .forEach((rawLine) => {
      const parsed = parsePastedNameLine(rawLine);
      if (!parsed) return;
      if (seen.has(parsed.normalized)) return;
      seen.add(parsed.normalized);
      lines.push(parsed);
    });

  return lines;
}

export function membershipFromSignupPlayer(player) {
  if (!player?.is_signed_up) return PASTE_MEMBERSHIP.NONE;
  if (player.signup_status === 'cancelled') return PASTE_MEMBERSHIP.CANCELLED;
  if (player.signup_status === 'waitlist' || player.waitlist_position) {
    return PASTE_MEMBERSHIP.WAITLIST;
  }
  if (player.signup_status === 'confirmed') return PASTE_MEMBERSHIP.CONFIRMED;
  return PASTE_MEMBERSHIP.NONE;
}

export function membershipFromLiveStatus(status) {
  if (status === undefined || status === null) return PASTE_MEMBERSHIP.NONE;
  return PASTE_MEMBERSHIP.ON_SESSION;
}

export function isAlreadyOnSession(membership) {
  return (
    membership === PASTE_MEMBERSHIP.CONFIRMED ||
    membership === PASTE_MEMBERSHIP.WAITLIST ||
    membership === PASTE_MEMBERSHIP.ON_SESSION
  );
}

function tokenScore(query, token, { allowInitial = false } = {}) {
  if (!query || !token) return 0;
  if (query === token) return 6;

  // "Anthony H": H may match last names starting with H. Never match the letter H
  // inside Cherizza, Nathan, Chow, etc.
  if (query.length <= 2) {
    if (allowInitial && query.length === 1 && token.startsWith(query)) return 3;
    return 0;
  }

  if (namesAreClose(query, token)) return 4;
  if (token.startsWith(query)) return 2;
  if (token.includes(query)) return 1;
  return 0;
}

export function scoreRosterNameMatch(parsed, player, { aliasByNormalized } = {}) {
  if (!parsed || !player) return 0;
  const aliasKey = parsed.normalized;
  if (aliasKey && aliasKey.length >= 2 && aliasByNormalized?.get(aliasKey) === player.id) {
    return 8;
  }
  const parts = playerNameParts(player);
  const queryFirst = normalizeNameKey(parsed.first);
  const queryLast = normalizeNameKey(parsed.last);
  const pastedFull = [queryFirst, queryLast].filter(Boolean).join(' ');

  let score = tokenScore(queryFirst, parts.first);
  if (queryFirst.length >= 3) {
    score = Math.max(score, tokenScore(queryFirst, parts.last));
  }

  if (queryLast) {
    score = Math.max(
      score,
      tokenScore(queryLast, parts.last, { allowInitial: queryLast.length === 1 })
    );
    if (queryLast.length >= 3) {
      score = Math.max(score, tokenScore(queryLast, parts.first));
      score = Math.max(
        score,
        scoreGuestNameMatch(parsed.first, parsed.last, player.first_name, player.last_name)
      );
    }
  }

  if (pastedFull && (pastedFull === parts.full || pastedFull === parts.reversed)) {
    return Math.max(score, 8);
  }
  if (
    pastedFull &&
    pastedFull.length >= 8 &&
    (namesAreClose(pastedFull, parts.full) || namesAreClose(pastedFull, parts.reversed))
  ) {
    score = Math.max(score, 5);
  }

  return score;
}

export function findUniqueAliasMatch(parsed, players, aliasByNormalized) {
  const key = parsed?.normalized;
  if (!key || key.length < 2 || !aliasByNormalized) return null;
  const playerId = aliasByNormalized.get(key);
  if (!playerId) return null;
  return (players || []).find((player) => player.id === playerId) || null;
}

function indexPlayersByExactName(players) {
  const byName = new Map();
  (players || []).forEach((player) => {
    const { full, reversed } = playerNameParts(player);
    [full, reversed].filter(Boolean).forEach((key) => {
      if (!byName.has(key)) byName.set(key, []);
      const list = byName.get(key);
      if (!list.some((existing) => existing.id === player.id)) {
        list.push(player);
      }
    });
  });
  return byName;
}

function uniqueFrom(matches) {
  if (!matches || matches.length !== 1) return null;
  return matches[0];
}

function findUniqueExactMatch(parsed, players, byExactName) {
  if (!parsed) return null;

  if (!parsed.last) {
    const token = normalizeNameKey(parsed.first);
    if (!token) return null;
    return uniqueFrom(
      (players || []).filter((player) => {
        const parts = playerNameParts(player);
        return parts.first === token || parts.last === token;
      })
    );
  }

  const pastedFull = [normalizeNameKey(parsed.first), normalizeNameKey(parsed.last)]
    .filter(Boolean)
    .join(' ');
  const pastedReversed = [normalizeNameKey(parsed.last), normalizeNameKey(parsed.first)]
    .filter(Boolean)
    .join(' ');
  const seen = new Set();
  const candidates = [];
  [pastedFull, pastedReversed].forEach((key) => {
    (byExactName.get(key) || []).forEach((player) => {
      if (seen.has(player.id)) return;
      seen.add(player.id);
      candidates.push(player);
    });
  });

  return uniqueFrom(candidates);
}

function firstOrLastIsClose(token, parts) {
  return namesAreClose(token, parts.first) || namesAreClose(token, parts.last);
}

function findUniqueCloseMatch(parsed, players) {
  if (!parsed) return null;
  const queryFirst = normalizeNameKey(parsed.first);
  const queryLast = normalizeNameKey(parsed.last);
  if (!queryFirst) return null;

  if (!queryLast) {
    if (queryFirst.length < 5) return null;
    return uniqueFrom(
      (players || []).filter((player) => firstOrLastIsClose(queryFirst, playerNameParts(player)))
    );
  }

  const pastedFull = [queryFirst, queryLast].join(' ');
  return uniqueFrom(
    (players || []).filter((player) => {
      const parts = playerNameParts(player);
      if (namesAreClose(pastedFull, parts.full) || namesAreClose(pastedFull, parts.reversed)) {
        return true;
      }
      const firstOk = queryFirst === parts.first || namesAreClose(queryFirst, parts.first);
      const lastOk = queryLast === parts.last || namesAreClose(queryLast, parts.last);
      return firstOk && lastOk && (namesAreClose(queryFirst, parts.first) || namesAreClose(queryLast, parts.last));
    })
  );
}

function findUniqueAutoMatch(parsed, players, byExactName, aliasByNormalized) {
  return findUniqueAliasMatch(parsed, players, aliasByNormalized)
    || findUniqueExactMatch(parsed, players, byExactName)
    || findUniqueCloseMatch(parsed, players);
}

function rowFromMatch({ parsed, index, player, getMembership }) {
  const membership = player ? getMembership(player) || PASTE_MEMBERSHIP.NONE : PASTE_MEMBERSHIP.NONE;
  const alreadyOn = player ? isAlreadyOnSession(membership) : false;

  return {
    id: `paste-row-${index}`,
    parsed,
    selectedPlayerId: player?.id || null,
    autoMatched: Boolean(player),
    membership,
    status: !player
      ? PASTE_ROW_STATUS.NEEDS_MATCH
      : alreadyOn
        ? PASTE_ROW_STATUS.ALREADY_ON_SESSION
        : PASTE_ROW_STATUS.READY,
    placement: null,
    waitlistPosition: null,
    blockedReason: null,
  };
}

/**
 * Match pasted names against the club roster.
 * Auto-selects a unique club paste alias, then a unique exact full-name (either order),
 * a unique first/last token, or a unique close typo (edit distance 1–2) when unambiguous.
 */
export function matchRosterPaste({
  text,
  players,
  getMembership,
  signedUpPlayerIds,
  sessionPlayerStatus,
  aliasByNormalized,
} = {}) {
  const roster = filterPlayersForHostSearch(players || [], {
    signedUpPlayerIds,
    sessionPlayerStatus,
  });
  const membershipOf = typeof getMembership === 'function'
    ? getMembership
    : () => PASTE_MEMBERSHIP.NONE;
  const lines = parseRosterPasteText(text);
  const byExactName = indexPlayersByExactName(roster);
  const claimedIds = new Set();

  const rows = lines.map((parsed, index) => {
    let player = findUniqueAutoMatch(parsed, roster, byExactName, aliasByNormalized);
    if (player && claimedIds.has(player.id)) {
      player = null;
    }
    if (player) claimedIds.add(player.id);
    return rowFromMatch({ parsed, index, player, getMembership: membershipOf });
  });

  return { rows, players: roster };
}

export function claimedPlayerIds(rows, exceptRowId = null) {
  return new Set(
    (rows || [])
      .filter((row) => row.selectedPlayerId && row.id !== exceptRowId)
      .map((row) => row.selectedPlayerId)
  );
}

export function getSuggestedPlayers(
  parsed,
  players,
  { excludeIds = new Set(), minScore = 2, aliasByNormalized } = {}
) {
  const roster = players || [];
  const scored = roster
    .filter((player) => !excludeIds.has(player.id))
    .map((player) => ({ player, score: scoreRosterNameMatch(parsed, player, { aliasByNormalized }) }))
    .filter(({ score }) => score >= minScore)
    .sort((a, b) => b.score - a.score);

  const suggested = scored.map(({ player }) => player);
  const suggestedIds = new Set(suggested.map((player) => player.id));
  const others = roster.filter(
    (player) => !excludeIds.has(player.id) && !suggestedIds.has(player.id)
  );

  return { suggested, others };
}

export function selectPlayerForRow(rows, rowId, player, getMembership) {
  if (!player) return rows;
  const membershipOf = typeof getMembership === 'function'
    ? getMembership
    : () => PASTE_MEMBERSHIP.NONE;

  return (rows || []).map((row) => {
    if (row.id !== rowId && row.selectedPlayerId === player.id) {
      return {
        ...row,
        selectedPlayerId: null,
        autoMatched: false,
        membership: PASTE_MEMBERSHIP.NONE,
        status: PASTE_ROW_STATUS.NEEDS_MATCH,
        placement: null,
        waitlistPosition: null,
        blockedReason: null,
      };
    }
    if (row.id !== rowId) return row;

    const membership = membershipOf(player) || PASTE_MEMBERSHIP.NONE;
    const alreadyOn = isAlreadyOnSession(membership);
    return {
      ...row,
      selectedPlayerId: player.id,
      autoMatched: false,
      membership,
      status: alreadyOn ? PASTE_ROW_STATUS.ALREADY_ON_SESSION : PASTE_ROW_STATUS.READY,
      placement: null,
      waitlistPosition: null,
      blockedReason: null,
    };
  });
}

export function clearPlayerForRow(rows, rowId) {
  return (rows || []).map((row) => {
    if (row.id !== rowId) return row;
    return {
      ...row,
      selectedPlayerId: null,
      autoMatched: false,
      membership: PASTE_MEMBERSHIP.NONE,
      status: PASTE_ROW_STATUS.NEEDS_MATCH,
      placement: null,
      waitlistPosition: null,
      blockedReason: null,
    };
  });
}

export function assignSignupPlacements(rows, {
  confirmedCount = 0,
  maxPlayers = 16,
  waitlistEnabled = true,
  waitlistCount = 0,
  signupSelection,
} = {}) {
  let confirmed = confirmedCount;
  let waitlist = waitlistCount;
  const hostSelect = isHostSelectSignup(signupSelection);

  return (rows || []).map((row) => {
    if (!row.selectedPlayerId || row.status === PASTE_ROW_STATUS.NEEDS_MATCH) {
      return {
        ...row,
        status: PASTE_ROW_STATUS.NEEDS_MATCH,
        placement: null,
        waitlistPosition: null,
        blockedReason: null,
      };
    }
    if (row.status === PASTE_ROW_STATUS.ALREADY_ON_SESSION || isAlreadyOnSession(row.membership)) {
      return {
        ...row,
        status: PASTE_ROW_STATUS.ALREADY_ON_SESSION,
        placement: PASTE_PLACEMENT.ALREADY_ON_SESSION,
        waitlistPosition: null,
        blockedReason: null,
      };
    }
    if (hostSelect) {
      waitlist += 1;
      return {
        ...row,
        status: PASTE_ROW_STATUS.READY,
        placement: PASTE_PLACEMENT.WAITLIST,
        waitlistPosition: waitlist,
        blockedReason: null,
      };
    }
    if (confirmed < maxPlayers) {
      confirmed += 1;
      return {
        ...row,
        status: PASTE_ROW_STATUS.READY,
        placement: PASTE_PLACEMENT.CONFIRMED,
        waitlistPosition: null,
        blockedReason: null,
      };
    }
    if (waitlistEnabled) {
      waitlist += 1;
      return {
        ...row,
        status: PASTE_ROW_STATUS.READY,
        placement: PASTE_PLACEMENT.WAITLIST,
        waitlistPosition: waitlist,
        blockedReason: null,
      };
    }
    return {
      ...row,
      status: PASTE_ROW_STATUS.BLOCKED,
      placement: PASTE_PLACEMENT.BLOCKED,
      waitlistPosition: null,
      blockedReason: 'Session is full and waitlist is off',
    };
  });
}

export function assignLivePlacements(rows) {
  return (rows || []).map((row) => {
    if (row.status === PASTE_ROW_STATUS.ALREADY_ON_SESSION || isAlreadyOnSession(row.membership)) {
      return { ...row, placement: PASTE_PLACEMENT.ALREADY_ON_SESSION, blockedReason: null };
    }
    if (row.selectedPlayerId && row.status === PASTE_ROW_STATUS.READY) {
      return { ...row, placement: PASTE_PLACEMENT.ADD, blockedReason: null };
    }
    return { ...row, placement: null, blockedReason: null };
  });
}

export function summarizePasteRows(rows) {
  const summary = {
    total: (rows || []).length,
    ready: 0,
    needsMatch: 0,
    alreadyOnSession: 0,
    blocked: 0,
    confirmed: 0,
    waitlist: 0,
    add: 0,
  };

  (rows || []).forEach((row) => {
    if (row.placement === PASTE_PLACEMENT.CONFIRMED) summary.confirmed += 1;
    if (row.placement === PASTE_PLACEMENT.WAITLIST) summary.waitlist += 1;
    if (row.placement === PASTE_PLACEMENT.ADD) summary.add += 1;
    if (row.status === PASTE_ROW_STATUS.NEEDS_MATCH) summary.needsMatch += 1;
    if (row.status === PASTE_ROW_STATUS.ALREADY_ON_SESSION) summary.alreadyOnSession += 1;
    if (row.status === PASTE_ROW_STATUS.BLOCKED) summary.blocked += 1;
    if (row.status === PASTE_ROW_STATUS.READY) summary.ready += 1;
  });

  return summary;
}

export function writablePasteRows(rows) {
  return (rows || []).filter(
    (row) =>
      row.selectedPlayerId &&
      (row.placement === PASTE_PLACEMENT.CONFIRMED ||
        row.placement === PASTE_PLACEMENT.WAITLIST ||
        row.placement === PASTE_PLACEMENT.ADD)
  );
}
